import {
  useCallback,
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { PatientPCCItemsDTO } from 'artifacts/dtos';
import { useGetRTMStatusByPatientId } from 'hooks/useApi';
import useIntakeFlags, { IntakeFlags } from 'hooks/useIntakeFlags';
import useCommuniqueState from 'hooks/useCommuniqueState';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import {
  getRtmCardCopy,
  getRtmChoice,
  getRtmModuleType,
} from 'utils/rtmChoice';

export type { IntakeFlags as RtmFlags };

export default function useIncompleteRtmRedirect(
  intakeFlags?: IntakeFlags | null,
  pccItems?: PatientPCCItemsDTO | null,
) {
  const dispatch = useDispatch();
  const { communiqueData } = useCommuniqueState();
  const { appointmentPatientId } = useSelector(
    (state: RootState) => state.appStateSettings,
  );
  const patientId = appointmentPatientId || communiqueData?.patientId;

  const { flags, isLoading: isLoadingFlags } = useIntakeFlags(
    !isNil(intakeFlags)
      ? { intakeFlags, pccItems }
      : undefined,
  );

  const shouldFetchRtmStatus = Boolean(
    flags.isRTMEnabledForOrg && flags.shouldShowRTM,
  );

  const {
    data: rtmStatus,
    isLoading: isLoadingRtmStatus,
    isFetching: isFetchingRtmStatus,
  } = useGetRTMStatusByPatientId(patientId, shouldFetchRtmStatus);

  const sessionRtmCompleted = communiqueData?.rtm?.hasCompleted === true;
  const sessionRtmKind = communiqueData?.rtm?.kind;

  const rtmChoice = useMemo(
    () => (shouldFetchRtmStatus ? getRtmChoice(rtmStatus) : null),
    [rtmStatus, shouldFetchRtmStatus],
  );

  const rtmCardCopy = useMemo(() => {
    if (rtmChoice) {
      return getRtmCardCopy(rtmChoice.kind);
    }

    if (sessionRtmCompleted && sessionRtmKind) {
      return getRtmCardCopy(sessionRtmKind);
    }

    return null;
  }, [rtmChoice, sessionRtmCompleted, sessionRtmKind]);

  const hasIncompleteRtm = rtmChoice != null && !rtmChoice.isComplete;
  const isRtmComplete = sessionRtmCompleted;
  const shouldShowRtmCard = (hasIncompleteRtm || isRtmComplete) && !isNil(rtmCardCopy);
  const isLoadingRtm = isLoadingFlags
    || (shouldFetchRtmStatus && (isLoadingRtmStatus || isFetchingRtmStatus));

  const goToCommuniqueChoice = useCallback(() => {
    dispatch(appStateSettingsAction({
      name: ModuleTypes.communiqueByPatientInfo,
      page: 'choice',
    }));
  }, [dispatch]);

  const startRtm = useCallback(() => {
    if (rtmChoice != null && !rtmChoice.isComplete) {
      dispatch(appStateSettingsAction({
        name: getRtmModuleType(rtmChoice.kind),
        page: null,
        rtmToken: rtmChoice.token ?? '',
        rtmKind: rtmChoice.kind,
      }));
    }
  }, [dispatch, rtmChoice]);

  return {
    goToCommuniqueChoice,
    hasIncompleteRtm,
    isLoadingRtm,
    isRtmComplete,
    rtmCardCopy,
    shouldShowRtmCard,
    startRtm,
  };
}
